import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { secureFetch } from '../supabaseService';

const PAGE_SIZE = 20;

const CHANNEL_FILTERS = [
  { id: '', i18nKey: 'notifChannelAll' },
  { id: 'whatsapp', i18nKey: 'notifChannelWhatsapp' },
  { id: 'email', i18nKey: 'notifChannelEmail' },
  { id: 'push', i18nKey: 'notifChannelPush' },
  { id: 'sms', i18nKey: 'notifChannelSms' },
];

const cardStyle = {
  background: 'linear-gradient(135deg, #0f2040, #0a1628)',
  border: '1px solid rgba(201,168,76,0.2)',
  borderRadius: '12px',
  padding: '16px',
};

const filterBtn = (active) => ({
  padding: '6px 12px',
  borderRadius: '999px',
  border: `1px solid ${active ? 'rgba(201,168,76,0.6)' : 'rgba(201,168,76,0.2)'}`,
  background: active ? 'rgba(201,168,76,0.18)' : 'rgba(0,0,0,0.2)',
  color: active ? '#c9a84c' : '#8896a8',
  fontSize: '11px',
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
});

function channelIcon(channel) {
  if (channel === 'whatsapp') return '📱';
  if (channel === 'sms') return '💬';
  if (channel === 'email') return '📧';
  if (channel === 'push') return '🔔';
  return '🔔';
}

function statusStyle(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'failed') {
    return { color: '#e94560', background: 'rgba(233,69,96,0.12)', border: '1px solid rgba(233,69,96,0.35)' };
  }
  if (normalized === 'pending') {
    return { color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)' };
  }
  return { color: '#4CAF50', background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.35)' };
}

function statusLabel(status, t) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'failed') return t('notifStatusFailed');
  if (normalized === 'pending') return t('notifStatusPending');
  return t('notifStatusSent');
}

function formatTimeAgo(iso) {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 60000) return 'Just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateMessage(text, max = 160) {
  const value = String(text || '').trim();
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

export default function NotificationCenter({ authToken: _authToken, apiEndpoint, title }) {
  const { t } = useLanguage();
  const [channel, setChannel] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const heading = title || t('notificationCenter');

  const loadNotifications = useCallback(async ({ nextLimit, appendChannel, silent = false }) => {
    if (!apiEndpoint) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(nextLimit) });
      if (appendChannel) params.set('channel', appendChannel);
      const data = await secureFetch(`${apiEndpoint}?${params.toString()}`);
      const rows = data?.notifications || [];
      setItems(rows);
      setHasMore(rows.length >= nextLimit);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setError(err?.message || t('genericError'));
      setItems([]);
      setHasMore(false);
    } finally {
      if (!silent) setLoading(false);
      setLoadingMore(false);
    }
  }, [apiEndpoint, t]);

  useEffect(() => {
    const silent = limit > PAGE_SIZE;
    if (silent) setLoadingMore(true);
    loadNotifications({ nextLimit: limit, appendChannel: channel, silent });
  }, [channel, limit, loadNotifications]);

  const filters = useMemo(() => CHANNEL_FILTERS.map((entry) => ({
    ...entry,
    label: t(entry.i18nKey),
  })), [t]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    setLimit((prev) => prev + PAGE_SIZE);
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ color: '#c9a84c', fontWeight: 700, fontSize: '15px', margin: '0 0 14px' }}>
        🔔 {heading}
      </h3>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        {filters.map((filter) => (
          <button
            key={filter.id || 'all'}
            type="button"
            style={filterBtn(channel === filter.id)}
            onClick={() => {
              setChannel(filter.id);
              setLimit(PAGE_SIZE);
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading && (
        <p style={{ color: '#8896a8', fontSize: 13, margin: 0 }}>{t('loading')}</p>
      )}

      {!loading && error && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <p style={{ color: '#e94560', fontSize: 13, margin: '0 0 10px' }}>{error}</p>
          <button
            type="button"
            onClick={() => loadNotifications({ nextLimit: limit, appendChannel: channel })}
            style={{
              background: 'rgba(201,168,76,0.15)',
              border: '1px solid rgba(201,168,76,0.35)',
              color: '#c9a84c',
              borderRadius: '8px',
              padding: '8px 14px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('retryLabel')}
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <p style={{ color: '#8896a8', fontSize: 13, textAlign: 'center', padding: '24px 12px', margin: 0 }}>
          {t('notifEmpty')}
        </p>
      )}

      {!loading && !error && items.length > 0 && (
        <div>
          {items.map((row) => {
            const badge = statusStyle(row.status);
            return (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  marginBottom: '8px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${badge.color}`,
                }}
              >
                <span style={{ fontSize: '20px', lineHeight: 1 }}>{channelIcon(row.channel)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', fontWeight: 700, margin: '0 0 4px' }}>
                      {row.type || '—'}
                    </p>
                    <span style={{
                      ...badge,
                      fontSize: '10px',
                      fontWeight: 700,
                      borderRadius: '999px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {statusLabel(row.status, t)}
                    </span>
                  </div>
                  <p style={{ color: '#c9c4b8', fontSize: '12px', margin: '0 0 6px', lineHeight: 1.45, wordBreak: 'break-word' }}>
                    {truncateMessage(row.message)}
                  </p>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>
                    {formatTimeAgo(row.created_at)}
                  </p>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                type="button"
                disabled={loadingMore}
                onClick={handleLoadMore}
                style={{
                  background: 'rgba(201,168,76,0.12)',
                  border: '1px solid rgba(201,168,76,0.35)',
                  color: '#c9a84c',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: loadingMore ? 'wait' : 'pointer',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {loadingMore ? t('loading') : t('notifLoadMore')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
